/**
 * ModulesContext — fetches the org's enabled module list and exposes
 * helpers to check whether a given module key or sidebar route is on.
 *
 * Used by:
 * - `Layout.js` to filter sidebar entries
 * - Page components to render the `<ModulePaywall />` when a route is disabled
 * - `SettingsPage` to render the toggle list
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const ModulesContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ModulesProvider({ children }) {
  const { token, user } = useAuth();
  const [catalogue, setCatalogue] = useState([]);
  const [enabledKeys, setEnabledKeys] = useState(null); // null = loading
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!token) {
      setEnabledKeys([]);
      setCatalogue([]);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCatalogue(res.data.modules || []);
      setEnabledKeys(res.data.enabled_keys || []);
    } catch (e) {
      // On error, fall back to "everything enabled" so we never accidentally
      // hide an existing customer's UI due to a transient failure.
      setEnabledKeys(null);
      setCatalogue([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh, user?.org_id]);

  const value = useMemo(() => {
    // Build a quick route-to-module map from the catalogue.
    const routeMap = {};
    for (const m of catalogue) {
      for (const r of m.routes || []) routeMap[r] = m.key;
    }
    const enabledSet = enabledKeys ? new Set(enabledKeys) : null;

    return {
      catalogue,
      enabledKeys: enabledKeys || [],
      loading,
      refresh,
      // Returns true if module is enabled, or if we couldn't load the list (fail-open).
      isModuleEnabled: (key) => enabledSet === null || enabledSet.has(key),
      // Helper: given a frontend route path, return the module key (or null).
      routeModule: (path) => routeMap[path] || null,
      // Helper: is the given path's module enabled?
      isRouteEnabled: (path) => {
        const k = routeMap[path];
        if (!k) return true; // unmapped routes are always allowed
        return enabledSet === null || enabledSet.has(k);
      },
      // Toggle a module (owners/admins only — backend enforces RBAC).
      toggleModule: async (key, enabled) => {
        const res = await axios.put(
          `${API}/modules/${key}`,
          { enabled },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEnabledKeys(res.data.enabled_keys || []);
        return res.data;
      },
    };
  }, [catalogue, enabledKeys, loading, refresh, token]);

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
}

export function useModules() {
  const ctx = useContext(ModulesContext);
  if (!ctx) {
    // Defensive default — should never happen if ModulesProvider wraps the app.
    return {
      catalogue: [], enabledKeys: [], loading: true, refresh: () => {},
      isModuleEnabled: () => true, routeModule: () => null,
      isRouteEnabled: () => true, toggleModule: async () => {},
    };
  }
  return ctx;
}
